import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Loader2, Mail, Lock, User, ArrowLeft, AlertCircle, CheckCircle, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginPage = ({ onClose, onLoginSuccess, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode === 'register' ? 'register' : 'login'); // 'login', 'register', 'forgot'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleGoogleLogin = () => {
    // CORRECT - use window.location.origin directly for OAuth redirect
    // This ensures auth works across all environments (preview, production, custom domains)
    const redirectUrl = window.location.origin;
    
    console.log('Google OAuth redirect URL:', redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Save to localStorage for persistence
      localStorage.setItem('flight380_user', JSON.stringify(response.data.user));
      onLoginSuccess(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { 
          email, 
          password, 
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          name: `${firstName.trim()} ${lastName.trim()}`
        },
        { withCredentials: true }
      );
      
      // Save to localStorage for persistence
      localStorage.setItem('flight380_user', JSON.stringify(response.data.user));
      onLoginSuccess(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess('Password reset instructions have been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-brand-600 via-purple-600 to-blue-600 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="w-full max-w-md my-4 relative z-10">
        <Card className="bg-white/95 backdrop-blur-xl shadow-2xl relative overflow-hidden border-0">
          {/* Colorful top border */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-brand-500 via-purple-500 to-cyan-500"></div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors z-10"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
          
          <CardHeader className="text-center pb-2 pt-8 px-4 sm:px-6">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gradient-to-br from-brand-500 via-purple-500 to-blue-500 rounded-2xl shadow-xl">
                <img src="/logo-f380.png" alt="Flight380" className="h-10 sm:h-12 brightness-0 invert" />
              </div>
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-brand-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              {mode === 'login' && 'Welcome Back'}
              {mode === 'register' && 'Create Account'}
              {mode === 'forgot' && 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-sm mt-2 text-slate-500">
              {mode === 'login' && 'Sign in to access your bookings'}
              {mode === 'register' && 'Join Flight380 for exclusive deals'}
              {mode === 'forgot' && 'Enter your email to reset your password'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4 px-4 sm:px-6 pb-6">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-sm">
                <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            {mode === 'login' && (
              <>
                {/* Google Login - Colorful */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-sm sm:text-base border-2 border-slate-200 hover:border-brand-400 hover:bg-gradient-to-r hover:from-brand-50 hover:to-purple-50 transition-all rounded-xl"
                  onClick={handleGoogleLogin}
                  type="button"
                >
                  <svg className="w-5 h-5 mr-2 sm:mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative py-2">
                  <Separator className="bg-slate-200" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-slate-400">
                    or continue with email
                  </span>
                </div>

                {/* Email Login Form - Colorful */}
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-sm font-semibold text-slate-700">Email Address</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-brand-100 to-purple-100 rounded">
                        <Mail className="h-4 w-4 text-brand-600" />
                      </div>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-12 border-2 border-slate-200 focus:border-brand-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-sm font-semibold text-slate-700">Password</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-purple-100 to-blue-100 rounded">
                        <Lock className="h-4 w-4 text-purple-600" />
                      </div>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-12 h-12 border-2 border-slate-200 focus:border-purple-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-brand-500 via-purple-500 to-blue-500 hover:from-brand-600 hover:via-purple-600 hover:to-blue-600 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>

                <div className="text-center space-y-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(null); }}
                    className="text-sm text-purple-600 hover:text-purple-700 hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                  <div className="text-sm text-slate-600">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => { setMode('register'); setError(null); }}
                      className="bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent font-bold hover:underline"
                    >
                      Sign up
                    </button>
                  </div>
                </div>
              </>
            )}

            {mode === 'register' && (
              <>
                {/* Google Sign Up - Colorful */}
                <Button 
                  variant="outline" 
                  className="w-full h-12 text-sm sm:text-base border-2 border-slate-200 hover:border-brand-400 hover:bg-gradient-to-r hover:from-brand-50 hover:to-purple-50 transition-all rounded-xl"
                  onClick={handleGoogleLogin}
                  type="button"
                >
                  <svg className="w-5 h-5 mr-2 sm:mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign up with Google
                </Button>

                <div className="relative py-2">
                  <Separator className="bg-slate-200" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-slate-400">
                    or
                  </span>
                </div>

                {/* Email Register Form - Colorful */}
                <form onSubmit={handleRegister} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="register-firstname" className="text-sm font-semibold text-slate-700">First Name *</Label>
                      <div className="relative mt-1.5">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded">
                          <User className="h-4 w-4 text-emerald-600" />
                        </div>
                        <Input
                          id="register-firstname"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-12 h-11 border-2 border-slate-200 focus:border-emerald-500 rounded-xl"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-lastname" className="text-sm font-semibold text-slate-700">Last Name *</Label>
                      <div className="relative mt-1.5">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-cyan-100 to-blue-100 rounded">
                          <User className="h-4 w-4 text-cyan-600" />
                        </div>
                        <Input
                          id="register-lastname"
                          type="text"
                          placeholder="Smith"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="pl-12 h-11 border-2 border-slate-200 focus:border-cyan-500 rounded-xl"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-email" className="text-sm font-semibold text-slate-700">Email Address</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-brand-100 to-purple-100 rounded">
                        <Mail className="h-4 w-4 text-brand-600" />
                      </div>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-11 border-2 border-slate-200 focus:border-brand-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-password" className="text-sm font-semibold text-slate-700">Password</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-purple-100 to-blue-100 rounded">
                        <Lock className="h-4 w-4 text-purple-600" />
                      </div>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-12 h-11 border-2 border-slate-200 focus:border-purple-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-confirm" className="text-sm font-semibold text-slate-700">Confirm Password</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-blue-100 to-cyan-100 rounded">
                        <Lock className="h-4 w-4 text-blue-600" />
                      </div>
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-12 h-11 border-2 border-slate-200 focus:border-blue-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500 hover:from-emerald-600 hover:via-cyan-600 hover:to-blue-600 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all mt-2"
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                </form>

                <div className="text-center text-sm text-slate-600 pt-2">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(null); }}
                    className="bg-gradient-to-r from-brand-600 to-purple-600 bg-clip-text text-transparent font-bold hover:underline"
                  >
                    Sign in
                  </button>
                </div>
              </>
            )}

            {mode === 'forgot' && (
              <>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email" className="text-sm font-semibold text-slate-700">Email Address</Label>
                    <div className="relative mt-1.5">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 p-1 bg-gradient-to-br from-amber-100 to-orange-100 rounded">
                        <Mail className="h-4 w-4 text-amber-600" />
                      </div>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-12 border-2 border-slate-200 focus:border-amber-500 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Send Reset Link
                  </Button>
                </form>

                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(null); setSuccess(null); }}
                  className="flex items-center justify-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline w-full pt-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
